/**
 * Higher-order component that wraps the specified component with an ErrorBoundary.
 * This helps to catch errors in the component tree and display a fallback UI when errors occur.
 * 
 * @template P - The props type of the wrapped component
 * @param {ComponentType<P>} WrappedComponent - The component to wrap with error boundary
 * @param {ReactNode} [fallback=null] - The fallback UI to render when an error is caught
 * @returns {(props: P) => JSX.Element} A new component with error boundary
 * 
 * @example
 * ```tsx
 * const SafeComponent = withErrorBoundary(MyComponent, <ErrorFallback />);
 * ```
 */

import React, { ComponentType, ReactNode } from "react";
import ErrorBoundary from "./index";

const withErrorBoundary = <P extends {}>(
  WrappedComponent: ComponentType<P>,
  fallback: ReactNode = null
) => {
  return (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );
};

export default withErrorBoundary;